import { FormEvent, useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, Lock } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { authApi } from "@/services/api";
import { cn } from "@/utils/cn";

interface Props {
  /** Ocultar em modo dev sem autenticação real */
  hidden?: boolean;
  className?: string;
}

export function ChangePasswordControl({ hidden, className }: Props) {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSuccess(false);
    setShowCurrent(false);
    setShowNew(false);
  }, [open]);

  if (hidden) return null;

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!currentPassword.trim()) {
      setError("Informe a senha atual.");
      return;
    }
    if (newPassword.length < 6) {
      setError("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("A confirmação da nova senha não confere.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("A nova senha deve ser diferente da senha atual.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await authApi.changePassword({
        currentPassword,
        newPassword,
      });
      if (!data.success) {
        setError(data.error ?? "Não foi possível alterar a senha.");
        return;
      }
      setSuccess(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Não foi possível alterar a senha. Tente novamente.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVisibility = (show: boolean, setShow: (v: boolean) => void) => (
    <button
      type="button"
      onClick={() => setShow(!show)}
      className="text-slate-400 hover:text-slate-600 transition-colors"
      tabIndex={-1}
      aria-label={show ? "Ocultar senha" : "Mostrar senha"}
    >
      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors",
          className,
        )}
        title="Alterar senha"
        aria-label="Alterar senha"
      >
        <KeyRound className="w-4 h-4" />
      </button>

      <Modal
        open={open}
        onClose={handleClose}
        title="Alterar senha"
        size="sm"
        footer={
          success ? (
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>
                Fechar
              </Button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={handleClose} disabled={submitting}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" form="change-password-form" loading={submitting}>
                Salvar senha
              </Button>
            </div>
          )
        }
      >
        {success ? (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
            Senha alterada com sucesso. Use a nova senha no próximo acesso.
          </p>
        ) : (
          <form id="change-password-form" onSubmit={handleSubmit} className="flex flex-col gap-4 -mt-1">
            <Input
              label="Senha atual"
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              leftIcon={<Lock className="w-4 h-4" />}
              rightIcon={toggleVisibility(showCurrent, setShowCurrent)}
            />
            <Input
              label="Nova senha"
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              hint="Mínimo de 6 caracteres"
              leftIcon={<Lock className="w-4 h-4" />}
              rightIcon={toggleVisibility(showNew, setShowNew)}
            />
            <Input
              label="Confirmar nova senha"
              type={showNew ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              leftIcon={<Lock className="w-4 h-4" />}
            />
            {error && (
              <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
          </form>
        )}
      </Modal>
    </>
  );
}
